import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
	Container,
	Paper,
	Typography,
	Box,
	TextField,
	Button,
	Alert,
	CircularProgress,
	Grid,
} from "@mui/material";
import { loadStripe } from "@stripe/stripe-js";
import {
	Elements,
	CardElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import ProgressStepper from "./ProgressStepper";

// Debug: Check what Stripe key is being loaded
const stripeKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
console.log("🔑 [FIRST-TIME-USER] Loading Stripe with key mode:", stripeKey?.startsWith('pk_live_') ? 'LIVE' : stripeKey?.startsWith('pk_test_') ? 'TEST' : 'UNKNOWN');

const stripePromise = loadStripe(stripeKey);

const cardElementOptions = {
	style: {
		base: {
			fontSize: "16px",
			color: "#424770",
			fontFamily: '"Inter", sans-serif',
			"::placeholder": {
				color: "#aab7c4",
			},
		},
		invalid: {
			color: "#9e2146",
		},
	},
	hidePostalCode: false,
};

function FirstTimeUserFormInner({
	onSuccess,
	onError,
	setupIntent,
	userData,
	onReady,
}) {
	const stripe = useStripe();
	const elements = useElements();
	const [isLoading, setIsLoading] = useState(false);
	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		email: "",
		...userData,
	});
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (stripe && elements) {
			onReady();
		}
	}, [stripe, elements, onReady]);

	const validateForm = () => {
		const newErrors = {};

		if (!formData.firstName.trim()) {
			newErrors.firstName = "First name is required";
		}

		if (!formData.lastName.trim()) {
			newErrors.lastName = "Last name is required";
		}

		if (!formData.email.trim()) {
			newErrors.email = "Email is required";
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
			newErrors.email = "Please enter a valid email address";
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleInputChange = (field) => (event) => {
		setFormData((prev) => ({
			...prev,
			[field]: event.target.value,
		}));

		// Clear error when user starts typing
		if (errors[field]) {
			setErrors((prev) => ({
				...prev,
				[field]: "",
			}));
		}
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		if (!validateForm()) {
			return;
		}

		setIsLoading(true);

		try {
			const cardElement = elements.getElement(CardElement);

			const { setupIntent: confirmedSetupIntent, error } =
				await stripe.confirmCardSetup(setupIntent, {
					payment_method: {
						card: cardElement,
						billing_details: {
							name: `${formData.firstName} ${formData.lastName}`,
							email: formData.email,
						},
					},
				});

			if (error) {
				throw new Error(error.message);
			}

			if (confirmedSetupIntent.status === "succeeded") {
				onSuccess({
					setupIntent: confirmedSetupIntent,
					userData: formData,
					paymentMethodId: confirmedSetupIntent.payment_method
				});
			} else {
				throw new Error("Payment setup failed");
			}
		} catch (error) {
			onError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<form onSubmit={handleSubmit}>
			<Typography
				variant="h4"
				component="h1"
				gutterBottom
				align="center"
				sx={{ fontWeight: "bold", fontSize: "24px", mb: 2 }}
			>
				First Time User
			</Typography>

			<Typography
				variant="body1"
				color="text.secondary"
				align="left"
				gutterBottom
				sx={{ mb: 4, fontSize: "16px" }}
			>
				Pebble is free to use. We only ask for a card to prevent misuse.
			</Typography>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
				{/* First Name */}
				<Box>
					<Typography
						variant="body2"
						sx={{ mb: 1, fontWeight: 600, fontSize: "16px" }}
					>
						First Name
					</Typography>
					<TextField
						fullWidth
						placeholder="John"
						value={formData.firstName}
						onChange={handleInputChange("firstName")}
						error={!!errors.firstName}
						helperText={errors.firstName}
						variant="outlined"
						sx={{
							"& .MuiOutlinedInput-root": {
								borderRadius: "10px",
								backgroundColor: "#ffffff",
								"& fieldset": {
									borderColor: "rgba(0,0,0,0.2)",
								},
							},
						}}
					/>
				</Box>

				{/* Last Name */}
				<Box>
					<Typography
						variant="body2"
						sx={{ mb: 1, fontWeight: 600, fontSize: "16px" }}
					>
						Last Name
					</Typography>
					<TextField
						fullWidth
						placeholder="Smith"
						value={formData.lastName}
						onChange={handleInputChange("lastName")}
						error={!!errors.lastName}
						helperText={errors.lastName}
						variant="outlined"
						sx={{
							"& .MuiOutlinedInput-root": {
								borderRadius: "10px",
								backgroundColor: "#ffffff",
								"& fieldset": {
									borderColor: "rgba(0,0,0,0.2)",
								},
							},
						}}
					/>
				</Box>

				{/* Email */}
				<Box>
					<Typography
						variant="body2"
						sx={{ mb: 1, fontWeight: 600, fontSize: "16px" }}
					>
						Email
					</Typography>
					<TextField
						fullWidth
						type="email"
						placeholder="john.smith@gmail.com"
						value={formData.email}
						onChange={handleInputChange("email")}
						error={!!errors.email}
						helperText={errors.email}
						variant="outlined"
						sx={{
							"& .MuiOutlinedInput-root": {
								borderRadius: "10px",
								backgroundColor: "#ffffff",
								"& fieldset": {
									borderColor: "rgba(0,0,0,0.2)",
								},
							},
						}}
					/>
				</Box>

				{/* Credit Card */}
				<Box>
					<Typography
						variant="body2"
						sx={{ mb: 1, fontWeight: 600, fontSize: "16px" }}
					>
						Credit Card
					</Typography>
					<Box
						sx={{
							p: 2,
							border: "1px solid rgba(0,0,0,0.2)",
							borderRadius: "10px",
							backgroundColor: "#ffffff",
							minHeight: "56px",
							display: "flex",
							alignItems: "center",
						}}
					>
						<CardElement
							options={cardElementOptions}
							style={{ width: "100%" }}
						/>
					</Box>
				</Box>

				{/* Continue Button */}
				<Button
					type="submit"
					fullWidth
					variant="contained"
					disabled={isLoading || !stripe}
					sx={{
						mt: 2,
						py: 2,
						backgroundColor: "#000000",
						borderRadius: "10px",
						fontSize: "16px",
						fontWeight: 600,
						textTransform: "none",
						"&:hover": {
							backgroundColor: "#333333",
						},
						"&:disabled": {
							backgroundColor: "#cccccc",
						},
					}}
				>
					{isLoading ? (
						<CircularProgress size={24} color="inherit" />
					) : (
						"Continue"
					)}
				</Button>
			</Box>
		</form>
	);
}

export default function FirstTimeUserForm({
	onSuccess,
	onError,
	setupIntent,
	userData = {},
}) {
	const [error, setError] = useState(null);
	const [isStripeLoading, setIsStripeLoading] = useState(true);

	const options = {
		clientSecret: setupIntent,
		appearance: { theme: "stripe" },
		loader: "auto",
	};

	const handleSuccess = (result) => {
		setError(null);
		onSuccess(result);
	};

	const handleError = (errorMessage) => {
		setError(errorMessage);
		onError(errorMessage);
	};

	return (
		<Container maxWidth="sm">
			<Box
				sx={{
					minHeight: "100vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					py: 4,
				}}
			>
				<Paper elevation={3} sx={{ p: 4, width: "100%", maxWidth: "400px" }}>
					<ProgressStepper currentStep={2} />

					{error && (
						<Alert severity="error" sx={{ mt: 2, mb: 3 }}>
							{error}
						</Alert>
					)}

					<Elements stripe={stripePromise} options={options}>
						<FirstTimeUserFormInner
							onError={handleError}
							onSuccess={handleSuccess}
							setupIntent={setupIntent}
							userData={userData}
							onReady={() => setIsStripeLoading(false)}
						/>
					</Elements>

					{isStripeLoading && (
						<Box
							sx={{
								display: "flex",
								justifyContent: "center",
								p: 2,
								position: "absolute",
								top: "50%",
								left: "50%",
								transform: "translate(-50%, -50%)",
							}}
						>
							<CircularProgress />
						</Box>
					)}
				</Paper>
			</Box>
		</Container>
	);
}
